import { inject } from '@angular/core';
import type { RedirectFunction } from '@angular/router';
import {
  hasSubClusterReadAccess,
  type PermissionClusterTree,
} from '../../features/access-control/models/permission-cluster.model';
import { UserPermissionTreeService } from '../../features/access-control/services/user-permission-tree.service';
import { UserStore } from '../store/user.store';

/**
 * Sub-clusters to try, in priority order, when resolving where an authenticated user
 * should land by default (after login, the app root, and any unmatched URL).
 * `Intake Table` (the Intake Queue) is tried first since it's the app's usual home
 * page — later entries are only reached when the user has no Read access to any
 * sub-cluster before them.
 */
const DEFAULT_LANDING_CANDIDATES: ReadonlyArray<{ subCluster: string; path: string }> = [
  { subCluster: 'Intake Table', path: '/intake' },
  { subCluster: 'Dashboard', path: '/dashboard' },
  { subCluster: 'Contract Table', path: '/contracts' },
  { subCluster: 'Reminders Table', path: '/reminders' },
  { subCluster: 'Agentic Search Table', path: '/search' },
  { subCluster: 'Access Control', path: '/access-control' },
];

/**
 * The first page in `DEFAULT_LANDING_CANDIDATES` `tree` grants Read access to, or
 * `/access-denied` if none of them do. Pure — takes the already-resolved permission
 * tree rather than injecting it, so it can be called from contexts that aren't
 * themselves running inside Angular's injection context (e.g. a component method).
 */
export function resolveDefaultLandingPath(tree: PermissionClusterTree): string {
  const landing = DEFAULT_LANDING_CANDIDATES.find((candidate) =>
    hasSubClusterReadAccess(tree, candidate.subCluster)
  );

  return landing?.path ?? '/access-denied';
}

/**
 * `redirectTo` function for the app root's empty path and the wildcard fallback, so
 * both always agree on where "home" is for the current user instead of assuming
 * every role can reach `/intake`. An unauthenticated user is sent to `/login`.
 */
export const defaultLandingRedirect: RedirectFunction = () => {
  const userStore = inject(UserStore);
  if (!userStore.isAuthenticated()) {
    return '/login';
  }

  const permissionTree = inject(UserPermissionTreeService);
  return resolveDefaultLandingPath(permissionTree.tree());
};
